import React from 'react';
import { AlertTriangle, X, Check, XCircle } from 'lucide-react';

const ConfirmationModal = ({
  isOpen = false,
  onClose = () => {},
  onConfirm = () => {},
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning", // warning, danger, info, success
  isLoading = false,
  confirmButtonColor = null
}) => {
  if (!isOpen) return null;

  // Get colors and icons based on type
  const getTypeConfig = (type) => {
    switch (type) {
      case 'danger':
        return {
          icon: <XCircle size={24} />,
          iconColor: '#f44336',
          iconBg: '#ffebee',
          confirmBg: '#f44336',
          confirmHover: '#d32f2f',
          confirmText: '#ffffff'
        };
      case 'success':
        return {
          icon: <Check size={24} />,
          iconColor: '#4caf50',
          iconBg: '#e8f5e8',
          confirmBg: '#4caf50',
          confirmHover: '#388e3c',
          confirmText: '#ffffff'
        };
      case 'info':
        return {
          icon: <AlertTriangle size={24} />,
          iconColor: '#2196f3',
          iconBg: '#e3f2fd',
          confirmBg: '#2196f3',
          confirmHover: '#1976d2',
          confirmText: '#ffffff'
        };
      default: // warning
        return {
          icon: <AlertTriangle size={24} />,
          iconColor: '#ff9800',
          iconBg: '#fff3e0',
          confirmBg: '#ff9800',
          confirmHover: '#f57c00',
          confirmText: '#ffffff'
        };
    }
  };

  const typeConfig = getTypeConfig(type);

  // Override confirm button color if provided
  const finalConfirmBg = confirmButtonColor || typeConfig.confirmBg;
  const finalConfirmHover = confirmButtonColor ? 
    (confirmButtonColor === '#f44336' ? '#d32f2f' : 
     confirmButtonColor === '#4caf50' ? '#388e3c' :
     confirmButtonColor === '#2196f3' ? '#1976d2' : '#f57c00') : 
    typeConfig.confirmHover;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <div 
      className="modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div 
        className="modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          maxWidth: '400px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          animation: 'modalSlideIn 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div 
          className="modal-header"
          style={{
            padding: '20px 24px 16px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: typeConfig.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: typeConfig.iconColor
              }}
            >
              {typeConfig.icon}
            </div>
            <h4 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#333',
              lineHeight: '1.3'
            }}>
              {title}
            </h4>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              color: '#666',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
              e.target.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#666';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div 
          className="modal-body"
          style={{
            padding: '20px 24px',
            fontSize: '14px',
            lineHeight: '1.5',
            color: '#555'
          }}
        >
          {message}
        </div>

        {/* Footer */}
        <div 
          className="modal-footer"
          style={{
            padding: '16px 24px 20px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}
        >
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#666',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#bbb';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#ddd';
              }
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: finalConfirmBg,
              color: typeConfig.confirmText,
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = finalConfirmHover;
                e.target.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = finalConfirmBg;
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            {isLoading && (
              <div 
                style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid transparent',
                  borderTop: '2px solid currentColor',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
            )}
            {confirmText}
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default ConfirmationModal;
